import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import moment, { Moment } from 'moment';

import DatePickerComponent, {
  DatePickerComponentProps,
} from '../DatePickerComponent';
import { DateTimeObject, Weekend } from './types';
import { isValidDayOfWeek } from './helpers';
import { useSelector } from 'react-redux';
import { selectInfoUser } from 'app/pages/App/slice/selectors';

export interface Props extends DatePickerComponentProps {
  isComboDiffTicket: boolean;
  searchStartDate?: Moment;
  searchEndDate?: Moment;
  showDateFrom: DateTimeObject;
  showDateTo: DateTimeObject;
  weekend: Weekend | null;
  onDefaultValue?: (value: Moment | null) => void;
  fireworkDay?: string[];
}

const TicketDateSelectable: React.FC<Props> = props => {
  const {
    isComboDiffTicket,
    searchStartDate,
    searchEndDate,
    showDateFrom,
    showDateTo,
    weekend,
    onDefaultValue,
    fireworkDay,
    ...restProps
  } = props;
  const defaultValue = useRef<Moment | undefined>(undefined);
  const isInit = useRef(true);
  const userInfo = useSelector(selectInfoUser);
  const minDateMoment = useMemo(() => {
    const possibleValues = [
      moment().startOf('day'),
      moment(showDateFrom?.dateOrigin, 'YYYY-MM-DD').startOf('day'),
      moment(
        userInfo?.businessPartner?.startContact?.date,
        'YYYY/MM/DD',
      ).startOf('day'),
    ];
    return possibleValues.reduce((max, item) => {
      if (max.isBefore(item)) return item;
      return max;
    });
  }, [showDateFrom?.dateOrigin]);

  const maxDateMoment = useMemo(() => {
    const possibleValues = [
      moment(showDateTo.dateOrigin, 'YYYY-MM-DD').startOf('day'),
      moment(userInfo?.businessPartner?.endContact?.date, 'YYYY/MM/DD').startOf(
        'day',
      ),
    ];
    return possibleValues.reduce((min, item) => {
      if (min.isAfter(item)) return item;
      return min;
    });
  }, [showDateFrom?.dateOrigin]);

  const findValidDate = useCallback(
    (
      start: Moment,
      end: Moment,
      type: 'START_DATE' | 'END_DATE' = 'START_DATE',
    ) => {
      const isSearchStartDate = type === 'START_DATE';
      let dateToCheck = isSearchStartDate ? start.clone() : end.clone();
      do {
        if (!weekend) return dateToCheck;
        if (isValidDayOfWeek(dateToCheck, weekend)) return dateToCheck;
        isSearchStartDate
          ? dateToCheck.add(1, 'days')
          : dateToCheck.subtract(1, 'days');
      } while (dateToCheck.isBetween(start, end, 'day', '[]'));
    },
    [weekend],
  );

  const datesHighlight = useMemo(() => {
    if (!searchStartDate || !searchEndDate) return [];

    let startDateValid: Moment | undefined = undefined;
    let endDateValid: Moment | undefined = undefined;

    if (!!fireworkDay) {
      if (!searchStartDate) {
        const firstValidDate = fireworkDay.find(date => {
          if (!weekend) return true;
          return isValidDayOfWeek(moment(date, 'YYYY-MM-DD'), weekend);
        });

        startDateValid = firstValidDate
          ? moment(firstValidDate, 'YYYY-MM-DD')
          : undefined;
      } else {
        const firstValidDate = fireworkDay.find(date => {
          if (moment(date, 'YYYY-MM-DD').isBefore(searchStartDate, 'day'))
            return false;

          if (!weekend) return true;

          return isValidDayOfWeek(moment(date, 'YYYY-MM-DD'), weekend);
        });

        startDateValid = firstValidDate
          ? moment(firstValidDate, 'YYYY-MM-DD')
          : undefined;
      }

      defaultValue.current = startDateValid;

      return [searchStartDate, searchEndDate];
    }

    const isSearchStartDateInRange = searchStartDate
      .startOf('day')
      .isBetween(minDateMoment, maxDateMoment, 'day', '[]');
    const isSearchEndDateInRange = searchEndDate
      .startOf('day')
      .isBetween(minDateMoment, maxDateMoment, 'day', '[]');

    if (!isSearchStartDateInRange && !isSearchEndDateInRange) {
      if (
        searchStartDate.startOf('day').isBefore(minDateMoment) &&
        searchEndDate.startOf('day').isAfter(maxDateMoment)
      ) {
        const startDateValid = findValidDate(minDateMoment, maxDateMoment);
        const endDateValid = findValidDate(
          minDateMoment,
          maxDateMoment,
          'END_DATE',
        );
        if (startDateValid && endDateValid) {
          defaultValue.current = startDateValid;
          return [startDateValid, endDateValid];
        }
      }
      return [searchStartDate, searchEndDate];
    }

    switch (true) {
      case !isSearchStartDateInRange && isSearchEndDateInRange:
        startDateValid = findValidDate(minDateMoment, searchEndDate);
        endDateValid = findValidDate(minDateMoment, searchEndDate, 'END_DATE');
        break;
      case isSearchStartDateInRange && !isSearchEndDateInRange:
        startDateValid = findValidDate(searchStartDate, maxDateMoment);
        endDateValid = findValidDate(
          searchStartDate,
          maxDateMoment,
          'END_DATE',
        );
        break;
      case isSearchStartDateInRange && isSearchEndDateInRange:
      default:
        startDateValid = findValidDate(searchStartDate, searchEndDate);
        endDateValid = findValidDate(
          searchStartDate,
          searchEndDate,
          'END_DATE',
        );
    }
    if (startDateValid && endDateValid) {
      defaultValue.current = startDateValid;
      return [startDateValid, endDateValid];
    }
    return [searchStartDate, searchEndDate];
  }, [
    searchStartDate,
    searchEndDate,
    fireworkDay,
    minDateMoment,
    maxDateMoment,
    weekend,
    findValidDate,
  ]);

  const disabledDate = (current: Moment) => {
    const isExistInFireworkDay = fireworkDay?.some(
      i => i === current.format('YYYY-MM-DD'),
    );

    if (!isExistInFireworkDay && !!fireworkDay) return true;

    const isOutOfRange =
      current.isBefore(minDateMoment, 'day') ||
      current.isAfter(maxDateMoment, 'day');

    const isValidDate = !weekend ? true : isValidDayOfWeek(current, weekend);

    return isOutOfRange || !isValidDate;
  };

  const defaultPickerValue = useMemo(() => {
    if (defaultValue.current) return defaultValue.current;
    if (searchStartDate && searchEndDate) return searchStartDate;
  }, [searchEndDate, searchStartDate]);

  useEffect(() => {
    if (isInit.current && onDefaultValue) {
      onDefaultValue(defaultValue?.current ?? null);
      isInit.current = false;
    }
  }, [onDefaultValue]);

  return (
    <DatePickerComponent
      allowClear={false}
      defaultValue={defaultValue.current}
      defaultPickerValue={defaultPickerValue}
      inputReadOnly
      disabledDate={disabledDate}
      datesHighlight={datesHighlight}
      format="DD/MM/YYYY"
      {...restProps}
    />
  );
};

export default React.memo(TicketDateSelectable);
